'use client'

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Play, FileText } from 'lucide-react'
import { galleryIcon } from '@/features/work-orders/constants/images'
import { uploadMultipleGroupPhotosDirect } from '@/features/group-photos/upload-client'
import successToast from '@/shared/lib/toast/success.toast'
import errorToast from '@/shared/lib/toast/error.toast'
import warningToast from '@/shared/lib/toast/warning.toast'

const EMPTY_FILE_ARRAY: File[] = []

function FilePreviewItem({
  file,
  isVideo,
  isPdf,
  onRemove,
}: {
  file: File
  isVideo: boolean
  isPdf?: boolean
  onRemove: () => void
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  if (!previewUrl && !isPdf) return null

  return (
    <div className="relative group">
      {isPdf ? (
        <div className="w-full h-20 rounded border overflow-hidden bg-muted flex items-center justify-center gap-2 p-2">
          <FileText className="w-8 h-8 text-muted-foreground" />
          <span className="text-xs truncate">{file.name}</span>
        </div>
      ) : isVideo ? (
        <div className="relative w-full h-20 rounded border overflow-hidden bg-black">
          <video src={previewUrl!} className="w-full h-full object-cover" muted />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
        </div>
      ) : (
        <Image
          src={previewUrl!}
          alt={`Preview ${file.name}`}
          className="w-full h-20 object-cover rounded border"
          width={80}
          height={80}
        />
      )}
      <button
        onClick={onRemove}
        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 z-10"
      >
        ×
      </button>
    </div>
  )
}

interface AddGroupPhotoModalProps {
  isOpen: boolean
  onClose: () => void
  groupId: string
  onUploadSuccess?: () => void
  initialFiles?: File[]
}

export default function AddGroupPhotoModal({
  isOpen,
  onClose,
  groupId,
  onUploadSuccess,
  initialFiles = EMPTY_FILE_ARRAY,
}: AddGroupPhotoModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const submittedRef = useRef(false)

  useEffect(() => {
    if (isOpen && initialFiles.length > 0) {
      setSelectedFiles(initialFiles)
    }
  }, [isOpen, initialFiles])

  const validateFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''

    const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
    if (unsupportedVideoExtensions.includes(fileExtension)) {
      return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`
    }
    const unsupportedVideoMimeTypes = [
      'video/x-msvideo',
      'video/x-matroska',
      'video/x-flv',
      'video/x-ms-wmv',
      'video/3gpp',
    ]
    if (unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
      return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`
    }

    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    const isPdf = file.type === 'application/pdf' || fileExtension === '.pdf'
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif']
    const videoExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']
    const isImageByExtension = imageExtensions.includes(fileExtension)
    const isVideoByExtension = videoExtensions.includes(fileExtension)

    if (!isImage && !isVideo && !isPdf && !isImageByExtension && !isVideoByExtension) {
      return `File "${file.name}" is not a supported image, video, or PDF format`
    }

    const MAX_IMAGE_SIZE = 50 * 1024 * 1024
    const MAX_VIDEO_SIZE = 500 * 1024 * 1024
    const MAX_PDF_SIZE = 50 * 1024 * 1024
    const isActuallyImage = isImage || isImageByExtension
    const isActuallyVideo = isVideo || isVideoByExtension
    const isActuallyPdf = isPdf || fileExtension === '.pdf'

    if (isActuallyImage && file.size > MAX_IMAGE_SIZE) {
      return `Image "${file.name}" is too large. Maximum size is ${MAX_IMAGE_SIZE / 1024 / 1024}MB`
    }
    if (isActuallyVideo && file.size > MAX_VIDEO_SIZE) {
      return `Video "${file.name}" is too large. Maximum size is ${MAX_VIDEO_SIZE / 1024 / 1024}MB`
    }
    if (isActuallyPdf && file.size > MAX_PDF_SIZE) {
      return `PDF "${file.name}" is too large. Maximum size is ${MAX_PDF_SIZE / 1024 / 1024}MB`
    }
    return null
  }

  const handleGalleryClick = () => {
    galleryInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const fileArray = Array.from(files)
      const validFiles: File[] = []
      const errors: string[] = []
      const MAX_TOTAL_SIZE = 1024 * 1024 * 1024
      const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0)

      fileArray.forEach((file) => {
        if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
          errors.push(`Total file size would exceed ${MAX_TOTAL_SIZE / 1024 / 1024}MB limit`)
          return
        }
        const error = validateFile(file)
        if (error) errors.push(error)
        else validFiles.push(file)
      })
      if (errors.length > 0) errors.forEach((e) => errorToast(e))
      if (validFiles.length > 0) setSelectedFiles((prev) => [...prev, ...validFiles])
      event.target.value = ''
    }
  }

  const handleSubmit = async () => {
    if (submittedRef.current) return
    if (selectedFiles.length === 0) {
      errorToast('Please select at least one photo, video, or PDF')
      return
    }
    if (!groupId) {
      errorToast('Group ID is required')
      return
    }

    try {
      submittedRef.current = true
      setIsUploading(true)

      const uploadResult = await uploadMultipleGroupPhotosDirect(groupId, selectedFiles)

      if (uploadResult.success) {
        const uploaded = uploadResult.data ?? []
        const count = uploaded.length
        const errs = ('errors' in uploadResult && Array.isArray(uploadResult.errors))
          ? uploadResult.errors
          : []
        const skipped =
          'skippedDuplicates' in uploadResult && Array.isArray(uploadResult.skippedDuplicates)
            ? uploadResult.skippedDuplicates
            : []

        if (count > 0) {
          const label = count === 1 ? (selectedFiles[0].type.startsWith('video/') ? 'video' : 'photo') : 'items'
          successToast(`Successfully uploaded ${count} ${label}${count > 1 ? 's' : ''}.`)
        }
        if (skipped.length > 0) {
          warningToast(`${skipped.length} file(s) were skipped (already exist for this group).`)
        }
        if (errs.length > 0) {
          errorToast(`${errs.length} file(s) could not be processed. ${errs.slice(0, 2).join(' ')}`)
        }
        if (count > 0) {
          onUploadSuccess?.()
          onClose()
        }
      } else {
        errorToast(
          ('message' in uploadResult ? uploadResult.message : undefined) || 'Failed to upload. Please try again.'
        )
      }
    } catch (error) {
      console.error('Error uploading group photos:', error)
      errorToast(error instanceof Error ? error.message : 'An error occurred while uploading.')
    } finally {
      submittedRef.current = false
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    setSelectedFiles([])
    onClose()
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (!isOpen && initialFiles.length === 0) setSelectedFiles([])
  }, [isOpen, initialFiles])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[100vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Photo / Video / PDF</DialogTitle>
          <DialogDescription>
            Select photos, videos, or PDFs to add to this volunteer group. They will appear in the timeline by date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Select media</Label>
            <div
              className="flex gap-8 justify-center py-4 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleGalleryClick}
            >
              <div className="flex flex-col items-center gap-2">
                <Image src={galleryIcon} alt="Gallery" className="w-10 h-10" />
                <span className="text-sm">Select files</span>
              </div>
            </div>
          </div>

          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*,video/*,.pdf,application/pdf"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <Label>Selected ({selectedFiles.length})</Label>
              <div className="grid grid-cols-3 gap-2 p-2 max-h-64 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <FilePreviewItem
                    key={`${file.name}-${index}`}
                    file={file}
                    isVideo={file.type.startsWith('video/')}
                    isPdf={file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')}
                    onRemove={() => removeFile(index)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} className="w-full">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="w-full"
            disabled={selectedFiles.length === 0 || isUploading}
            showLoader={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
